import { DataProvider } from '@refinedev/core';
import { axiosInstance } from '@/api/client';
import { API_CONFIG } from '@/config/env';

/**
 * Normalize resource name for API URLs
 * Converts dot notation (e.g., "iam.users") to path format (e.g., "iam/users")
 */
const normalizeResource = (resource: string): string => {
  return resource.replace(/\./g, '/');
};

/**
 * Serialize query params the way the backend expects: arrays as repeated
 * keys (`ids=1&ids=2`, no brackets), `undefined` skipped, `null` as a bare
 * key. Axios' default serializer emits `ids[]=1`, which FastAPI rejects.
 */
export const buildQueryString = (query: Record<string, unknown>): string => {
  const parts: string[] = [];
  const append = (key: string, value: unknown) => {
    if (value === undefined) return;
    if (value === null) {
      parts.push(encodeURIComponent(key));
      return;
    }
    parts.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    );
  };
  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => append(key, item));
    } else {
      append(key, value);
    }
  });
  return parts.join('&');
};

type BodyMethod = 'post' | 'put' | 'patch';

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const pag = pagination as
      | {
          currentPage?: number;
          page?: number;
          pageSize?: number;
          mode?: string;
        }
      | undefined;
    const currentPage = pag?.currentPage ?? pag?.page ?? 1;
    const pageSize = pag?.pageSize ?? 20;
    const mode = pag?.mode ?? 'server';

    const normalizedResource = normalizeResource(resource);

    const params: Record<string, string | number | boolean | undefined> = {};

    // Pagination
    if (mode === 'server') {
      params.offset = (currentPage - 1) * pageSize;
      params.limit = pageSize;
    }

    // Sorting
    if (sorters && sorters.length > 0) {
      params.sort = sorters
        .map((s) => `${s.order === 'asc' ? '+' : '-'}${s.field}`)
        .join(',');
    }

    // Filters
    if (filters) {
      filters.forEach((filter) => {
        if ('field' in filter && filter.value !== undefined) {
          const { field, operator, value } = filter;

          // Special handling for full-text search parameter
          if (field === 'q') {
            params.q = value;
            return;
          }

          switch (operator) {
            case 'eq':
              params[`${field}[eq]`] = value;
              break;
            case 'ne':
              params[`${field}[ne]`] = value;
              break;
            case 'contains':
              params[`${field}[contains]`] = value;
              break;
            case 'gt':
              params[`${field}[gt]`] = value;
              break;
            case 'gte':
              params[`${field}[ge]`] = value;
              break;
            case 'lt':
              params[`${field}[lt]`] = value;
              break;
            case 'lte':
              params[`${field}[le]`] = value;
              break;
            case 'null':
              params[`${field}[is_null]`] = true;
              break;
            case 'nnull':
              params[`${field}[is_null]`] = false;
              break;
            case 'in':
              params[`${field}[in]`] = Array.isArray(value)
                ? value.join(',')
                : value;
              break;
            case 'nin':
              params[`${field}[not_in]`] = Array.isArray(value)
                ? value.join(',')
                : value;
              break;
            default:
              params[field] = value;
          }
        }
      });
    }

    const path =
      meta?.trailingSlash === false
        ? normalizedResource
        : normalizedResource + '/';

    const { data, headers } = await axiosInstance.get(path, {
      params,
    });

    // Get total count from header
    const total = headers['x-total-count']
      ? parseInt(headers['x-total-count'], 10)
      : data.length;

    return {
      data,
      total,
    };
  },

  getOne: async ({ resource, id, meta }) => {
    const { data } = await axiosInstance.get(
      `${normalizeResource(resource)}/${id}`,
      { headers: meta?.headers }
    );
    return { data };
  },

  getMany: async ({ resource, ids, meta }) => {
    const normalizedResource = normalizeResource(resource);
    const { data } = await axiosInstance.get(normalizedResource + '/', {
      params: { 'id[in]': ids.join(','), ...meta?.params },
    });
    return { data };
  },

  create: async ({ resource, variables, meta }) => {
    const normalizedResource = normalizeResource(resource);
    const { data } = await axiosInstance.post(
      `${normalizedResource}/`,
      variables,
      {
        params: meta?.params,
      }
    );
    return { data };
  },

  update: async ({ resource, id, variables, meta }) => {
    const method = (meta?.method as BodyMethod) ?? 'patch';
    const { data } = await axiosInstance[method](
      `${normalizeResource(resource)}/${id}`,
      variables,
      { headers: meta?.headers }
    );
    return { data };
  },

  deleteOne: async ({ resource, id, variables, meta }) => {
    const { data } = await axiosInstance.delete(
      `${normalizeResource(resource)}/${id}`,
      { data: variables, headers: meta?.headers }
    );
    return { data };
  },

  getApiUrl: () => {
    return API_CONFIG.baseURL;
  },

  custom: async ({ url, method, payload, query, headers }) => {
    const requestUrl = url.replace(/\./g, '/');

    switch (method) {
      case 'put':
      case 'post':
      case 'patch': {
        const { data } = await axiosInstance[method](requestUrl, payload, {
          headers,
        });
        return { data };
      }
      case 'delete': {
        const { data } = await axiosInstance.delete(requestUrl, {
          data: payload,
          headers,
        });
        return { data };
      }
      default: {
        const queryString = query
          ? buildQueryString(query as Record<string, unknown>)
          : '';
        const { data } = await axiosInstance.get(
          queryString ? `${requestUrl}?${queryString}` : requestUrl,
          { headers }
        );
        return { data };
      }
    }
  },
};
